import { Anchor, Code, Stack, Text } from '@mantine/core'

import { formatJson, type HubspotEventDetails } from '../utils/hubspot-event-details'

type Props = {
  details: HubspotEventDetails
  message?: string | null
  variant: 'success' | 'failure' | 'neutral'
  action?: string | null
}

export const HubspotApiDetails = ({ details, message, variant, action }: Props) => {
  const { api, operation, input, messageContext, response, hubspotRecordUrl, hubspotRecordId } =
    details
  const isFailure = variant === 'failure'
  const recordLabel = hubspotRecordId ? `Open in HubSpot (${hubspotRecordId})` : 'Open in HubSpot'

  return (
    <Stack gap='sm'>
      {message && (
        <Text
          size='sm'
          c={isFailure ? 'red' : variant === 'success' ? 'green' : undefined}
          fw={500}
        >
          {message}
        </Text>
      )}

      {hubspotRecordUrl && (
        <Anchor href={hubspotRecordUrl} target='_blank' rel='noopener noreferrer' size='sm'>
          {recordLabel}
          {action ? ` · ${action}` : ''}
        </Anchor>
      )}

      {(operation || api?.method || api?.url) && (
        <Text size='xs' ff='monospace' c='dimmed'>
          {operation ?? `${api?.method ?? 'POST'} ${api?.url ?? ''}`}
        </Text>
      )}

      {isFailure && api && (
        <Stack gap={4}>
          {api.statusCode != null && (
            <Text size='sm' fw={600} c='red'>
              HTTP {api.statusCode}
              {api.statusText ? ` ${api.statusText}` : ''}
            </Text>
          )}
          {api.message && (
            <Text size='sm' c='red'>
              {api.message}
            </Text>
          )}
          {api.category && (
            <Text size='xs' c='dimmed'>
              Category: {api.category}
            </Text>
          )}
        </Stack>
      )}

      {input != null && (
        <Stack gap={4}>
          <Text size='xs' fw={700} tt='uppercase' c='dimmed'>
            Input sent to HubSpot
          </Text>
          <Code
            block
            style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 240, overflow: 'auto' }}
          >
            {formatJson(input)}
          </Code>
        </Stack>
      )}

      {messageContext != null && Object.keys(messageContext).length > 0 && (
        <Stack gap={4}>
          <Text size='xs' fw={700} tt='uppercase' c='dimmed'>
            Trace context
          </Text>
          <Code
            block
            style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 120, overflow: 'auto' }}
          >
            {formatJson(messageContext)}
          </Code>
        </Stack>
      )}

      {isFailure && response != null && (
        <Stack gap={4}>
          <Text size='xs' fw={700} tt='uppercase' c='dimmed'>
            HubSpot API response
          </Text>
          <Code
            block
            style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 280, overflow: 'auto' }}
          >
            {formatJson(response)}
          </Code>
        </Stack>
      )}

      {!isFailure && response != null && (
        <Stack gap={4}>
          <Text size='xs' fw={700} tt='uppercase' c='dimmed'>
            HubSpot response
          </Text>
          <Code
            block
            style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 480, overflow: 'auto' }}
          >
            {formatJson(response)}
          </Code>
        </Stack>
      )}
    </Stack>
  )
}
